/**
 * CuratedFirmsBrowser — a filterable index of the 132-firm curated
 * salary-bands catalog. Sits alongside the accelerator cards on
 * `/employers` so families can find a specific firm by sector or
 * geography instead of guessing which country tab it lives in.
 *
 * Reads /api/employers/curated (returns items + facets). Filters are
 * client-side once the payload is in — the facet counts DON'T shrink
 * with filters, which is deliberate: a family searching "banking"
 * should still see the total tech count so they can rebalance their
 * mental model of the catalog.
 */
import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { ShieldCheck, Loader2, GitCompareArrows, X, Briefcase } from "lucide-react";
import FirmLogo from "@/components/app/FirmLogo";

const SECTOR_LABELS = {
  tech: "Big Tech",
  law: "Law",
  banking: "Investment Banking",
  hedge_fund: "Hedge Fund",
  prop_trading: "Prop Trading",
  consulting: "Consulting",
  public: "Public sector",
  engineering: "Engineering",
  aerospace: "Aerospace",
  automotive: "Automotive",
  media: "Media",
  telecoms: "Telecoms",
  semiconductors: "Semiconductors",
  fmcg: "FMCG",
  consumer: "Consumer",
  "consumer-engineering": "Consumer engineering",
  ngo: "NGO",
  healthcare: "Healthcare",
  pharma: "Pharma",
  "life-sciences": "Life Sciences",
  energy: "Energy",
  education: "Education",
  arts: "Arts & Culture",
  finance: "Finance",
  insurance: "Insurance",
  retail: "Retail",
  fashion: "Fashion",
  travel: "Travel",
};

const labelFor = (key) => SECTOR_LABELS[key] || (key || "Other").replace(/-/g, " ");

export default function CuratedFirmsBrowser() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedSectors, setSelectedSectors] = useState(new Set());
  const [q, setQ] = useState("");
  // Shift-click multi-select for Compare. Up to 4 firms is the max
  // /employers/compare will render nicely side-by-side — beyond that
  // the mini-cards start to squeeze on mobile. The picker is a soft
  // ceiling: we simply refuse the 5th selection and toast the user.
  const [selectedFirms, setSelectedFirms] = useState(new Set());
  const [showPicker, setShowPicker] = useState(false);
  const navigate = useNavigate();
  const MAX_COMPARE = 4;

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data: resp } = await api.get("/employers/curated");
        if (!cancelled) { setData(resp); setLoading(false); }
      } catch {
        if (!cancelled) { setData({ items: [], facets: { sectors: [], countries: [] }, total: 0 }); setLoading(false); }
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const filtered = useMemo(() => {
    if (!data?.items) return [];
    const qn = q.trim().toLowerCase();
    return data.items.filter((f) => {
      if (selectedSectors.size > 0 && !selectedSectors.has(f.sector)) return false;
      if (qn) {
        const hay = `${f.firm_name || ""} ${f.slug || ""} ${(f.countries || []).join(" ")}`.toLowerCase();
        if (!hay.includes(qn)) return false;
      }
      return true;
    });
  }, [data, selectedSectors, q]);

  const toggleSector = (key) => {
    setSelectedSectors((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key); else next.add(key);
      return next;
    });
  };
  const clearFilters = () => { setSelectedSectors(new Set()); setQ(""); };

  // Card click semantics:
  //   • plain click → open the firm's page (usual behaviour)
  //   • shift/meta/ctrl click OR the compare mode is active
  //     → toggle the firm in the compare selection instead
  // This mirrors the "hold shift to multi-select in a file browser"
  // convention families already know. `showPicker` also engages a
  // dedicated compare toolbar so touch users (no shift key) have a
  // path in.
  const onCardClick = (slug, evt) => {
    const multi = evt.shiftKey || evt.metaKey || evt.ctrlKey || showPicker;
    if (!multi) return; // let the <Link> navigate normally
    evt.preventDefault();
    setSelectedFirms((prev) => {
      const next = new Set(prev);
      if (next.has(slug)) {
        next.delete(slug);
      } else if (next.size >= MAX_COMPARE) {
        // Soft cap. Silent — the disabled state on the button and the
        // toolbar counter already tell the story; no need for a toast
        // interrupting flow.
        return prev;
      } else {
        next.add(slug);
      }
      return next;
    });
    if (!showPicker) setShowPicker(true);
  };
  const clearCompare = () => { setSelectedFirms(new Set()); setShowPicker(false); };
  const goCompare = () => {
    if (selectedFirms.size < 2) return;
    navigate(`/employers/compare?slugs=${Array.from(selectedFirms).join(",")}`);
  };

  if (loading) {
    return (
      <div data-testid="curated-firms-loading" className="flex items-center gap-2 text-stone-500 text-sm py-8">
        <Loader2 className="h-4 w-4 animate-spin" /> Loading curated firms…
      </div>
    );
  }
  if (!data || (data.total || 0) === 0) return null;

  const hasFilters = selectedSectors.size > 0 || q.trim().length > 0;

  return (
    <section data-testid="curated-firms-browser" className="mt-16 border-t border-stone-200 pt-10">
      <div className="flex items-baseline justify-between gap-3 flex-wrap">
        <div>
          <div className="label-uppercase text-emerald-700">Curated firm-published salary bands</div>
          <h2 className="mt-2 font-editorial text-3xl sm:text-4xl leading-tight">
            Browse every firm we've verified.
          </h2>
          <p className="mt-3 text-stone-600 max-w-2xl leading-relaxed">
            {data.total} firms with role-by-role pay bands cross-checked against
            the firm's own careers page, Levels.fyi, Legal Cheek and
            eFinancialCareers. Every card links to the same
            <span className="font-mono"> Verified Q3 2026 </span>
            provenance you'd see on the employer page.
          </p>
        </div>
        <div className="text-[11px] font-underground-sc tracking-wider text-stone-400">
          {filtered.length} shown
        </div>
      </div>

      {/* Search + sector chips */}
      <div className="mt-6 space-y-3">
        <input
          type="text"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          data-testid="curated-firms-search"
          placeholder="Search firm name — e.g. Ericsson, ASML, MSF"
          className="w-full max-w-md rounded-md border border-stone-300 bg-white px-3 py-2 text-sm focus:outline-none focus:border-emerald-500 focus:ring-1 focus:ring-emerald-500"
        />
        <div className="flex flex-wrap gap-1.5">
          {(data.facets?.sectors || []).map((s) => {
            const active = selectedSectors.has(s.key);
            return (
              <button
                key={s.key}
                type="button"
                onClick={() => toggleSector(s.key)}
                data-testid={`curated-firms-sector-${s.key}`}
                className={`inline-flex items-center gap-1 rounded-full border px-2.5 py-1 text-[11px] font-mono transition ${
                  active
                    ? "border-emerald-700 bg-emerald-700 text-white"
                    : "border-stone-300 bg-white text-stone-700 hover:border-emerald-500 hover:text-emerald-800"
                }`}
              >
                {labelFor(s.key)}
                <span className={active ? "text-emerald-100" : "text-stone-400"}>
                  {s.count}
                </span>
              </button>
            );
          })}
          {hasFilters && (
            <button
              type="button"
              onClick={clearFilters}
              data-testid="curated-firms-clear"
              className="inline-flex items-center rounded-full border border-stone-400 bg-stone-50 px-2.5 py-1 text-[11px] font-mono text-stone-600 hover:bg-stone-100"
            >
              Clear
            </button>
          )}
        </div>
      </div>

      {/* Compare toolbar — shows when at least one firm is picked, or
          when the user has entered pick-mode via the top-right toggle.
          Sticky so it stays reachable while scrolling long grids. */}
      <div className="mt-6 flex flex-wrap items-center gap-2 justify-between">
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={() => setShowPicker((v) => !v)}
            data-testid="curated-firms-picker-toggle"
            className={`inline-flex items-center gap-1.5 rounded-full border px-3 py-1.5 text-[11px] font-underground-sc tracking-wider transition ${
              showPicker
                ? "border-[#4C1D95] bg-[#4C1D95] text-white"
                : "border-stone-300 bg-white text-stone-700 hover:border-[#4C1D95] hover:text-[#4C1D95]"
            }`}
          >
            <GitCompareArrows className="h-3 w-3" />
            {showPicker ? "Picking to compare" : "Compare firms"}
          </button>
          {showPicker && (
            <span className="text-[11px] font-mono text-stone-500">
              tap a firm to add · or shift-click anywhere
            </span>
          )}
        </div>
        {selectedFirms.size > 0 && (
          <div
            data-testid="curated-firms-compare-tray"
            className="inline-flex items-center gap-2 rounded-full border border-emerald-300 bg-emerald-50 px-3 py-1.5"
          >
            <span className="text-[11px] font-underground-sc tracking-wider text-emerald-800">
              {selectedFirms.size} selected
            </span>
            <button
              type="button"
              onClick={goCompare}
              disabled={selectedFirms.size < 2}
              data-testid="curated-firms-go-compare"
              className={`text-[11px] font-underground-sc tracking-wider rounded-full px-2 py-0.5 border transition ${
                selectedFirms.size < 2
                  ? "border-stone-300 bg-stone-100 text-stone-400 cursor-not-allowed"
                  : "border-emerald-700 bg-emerald-700 text-white hover:bg-emerald-800"
              }`}
              title={selectedFirms.size < 2 ? "Pick at least 2 firms to compare" : `Compare ${selectedFirms.size} firms side-by-side`}
            >
              Compare →
            </button>
            <button
              type="button"
              onClick={clearCompare}
              data-testid="curated-firms-clear-compare"
              className="p-0.5 rounded-full text-emerald-800 hover:bg-emerald-100"
              title="Clear compare selection"
            >
              <X className="h-3 w-3" />
            </button>
          </div>
        )}
      </div>

      {/* Firm grid */}
      <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
        {filtered.map((f) => {
          const isPicked = selectedFirms.has(f.slug);
          return (
            <Link
              key={f.slug}
              to={`/employers/${f.slug}`}
              onClick={(e) => onCardClick(f.slug, e)}
              data-testid={`curated-firm-card-${f.slug}`}
              data-picked={isPicked ? "true" : "false"}
              className={`group rounded-lg border bg-white p-4 transition relative ${
                isPicked
                  ? "border-emerald-600 ring-2 ring-emerald-200"
                  : "border-stone-200 hover:border-emerald-500 hover:shadow-sm"
              }`}
            >
              {isPicked && (
                <span className="absolute top-2 right-2 rounded-full bg-emerald-600 text-white text-[9px] font-underground-sc tracking-wider px-1.5 py-0.5">
                  Picked
                </span>
              )}
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0 flex-1 flex items-start gap-2.5">
                  <FirmLogo
                    slug={f.slug}
                    name={f.firm_name}
                    sector={f.sector}
                    IconFallback={Briefcase}
                    size={32}
                  />
                  <div className="min-w-0 flex-1">
                    <div className="text-sm font-semibold text-stone-900 truncate group-hover:text-emerald-800">
                      {f.firm_name}
                    </div>
                    <div className="mt-0.5 text-[11px] font-underground-sc tracking-wider text-stone-500">
                      {labelFor(f.sector)}
                    </div>
                  </div>
                </div>
                {!isPicked && f.freshness && !f.freshness.is_stale && (
                  <span
                    title={`Verified ${f.freshness.as_of}`}
                    className="inline-flex items-center gap-0.5 rounded-full border border-emerald-300 bg-emerald-50 px-1.5 py-0.5 text-[9px] font-underground-sc tracking-wider text-emerald-800"
                  >
                    <ShieldCheck className="h-2.5 w-2.5" />
                    fresh
                  </span>
                )}
                {!isPicked && f.freshness?.is_stale && (
                  <span
                    title="This firm is overdue for a refresh"
                    className="inline-flex items-center rounded-full border border-amber-300 bg-amber-50 px-1.5 py-0.5 text-[9px] font-underground-sc tracking-wider text-amber-800"
                  >
                    refresh due
                  </span>
                )}
              </div>
              <div className="mt-2 text-[11px] text-stone-600 truncate">
                {(f.countries || []).slice(0, 3).join(" · ")}
                {f.countries && f.countries.length > 3 && ` +${f.countries.length - 3}`}
              </div>
              <div className="mt-1 text-[10px] font-mono text-stone-400">
                {f.band_count} band{f.band_count === 1 ? "" : "s"} · {f.location_count} location{f.location_count === 1 ? "" : "s"}
              </div>
              <div
                className="mt-3 pt-2 border-t border-stone-100 text-[11px] text-[#4C1D95] hover:underline"
                onClick={(e) => { e.preventDefault(); e.stopPropagation(); window.location.assign(`/how-to-get-into/${f.slug}`); }}
                data-testid={`curated-firm-guide-link-${f.slug}`}
              >
                How to get into {f.firm_name} →
              </div>
            </Link>
          );
        })}
      </div>

      {filtered.length === 0 && (
        <div
          data-testid="curated-firms-empty"
          className="mt-6 rounded-lg border border-dashed border-stone-300 bg-stone-50 p-8 text-center text-stone-500 text-sm"
        >
          No firms match those filters. Try clearing sectors or the search.
        </div>
      )}
    </section>
  );
}
